import { EnumBetType } from '@bg-shared/enums/BetType';
import { format, getHours, parseISO, setHours, startOfDay, addDays } from 'date-fns';
import { DEFAULT_DATE_FORMAT, DEFAULT_TIME_FULL_FORMAT } from '@bg-shared/constants';
import { omit, get } from 'lodash-unified';
import {
    IBetHistoryDay,
    IBetHistoryParams,
    IBetHistoryParamsFormatted,
    IBetHistoryResponse,
    IBetHistoryResponseMap,
} from './interfaces';

class ReportsFormatter {
    private readonly DATE_PARAM_FORMAT = `${DEFAULT_DATE_FORMAT} ${DEFAULT_TIME_FULL_FORMAT}`;

    private formatDate(time: string, timezone: number, dateFormat: string): string {
        const date = parseISO(time);
        const dateTimezoneAdjusted = setHours(date, getHours(date) + timezone);
        return format(dateTimezoneAdjusted, dateFormat);
    }

    private groupItemsByDay<T>(data: {
        items: T[];
        timezone: number;
        dateFormat: string;
        timePath: string;
    }): IBetHistoryDay<T>[] {
        return data.items.reduce<IBetHistoryDay<T>[]>((acc, item) => {
            const time = get(item, data.timePath);

            if (!time || typeof time !== 'string') {
                console.warn(`Missing or invalid time for item:`, item);
                return acc;
            }

            const dayString = this.formatDate(time, data.timezone, data.dateFormat);
            const index = acc.findIndex((accItem) => accItem.day === dayString);

            if (index === -1) {
                acc.push({
                    day: dayString,
                    data: [item],
                });
            } else {
                acc[index].data.push(item);
            }
            return acc;
        }, []);
    }

    public formatParams(params: IBetHistoryParams): IBetHistoryParamsFormatted {
        if (params.useExactTime) {
            return {
                ...omit(params, 'dates', 'dateFormat', 'useExactTime'),
                from: format(params.dates[0], this.DATE_PARAM_FORMAT),
                till: format(params.dates[1], this.DATE_PARAM_FORMAT),
            };
        }

        return {
            ...omit(params, 'dates', 'dateFormat', 'useExactTime'),
            from: format(startOfDay(params.dates[0]), this.DATE_PARAM_FORMAT),
            till: format(addDays(startOfDay(params.dates[1]), 1), this.DATE_PARAM_FORMAT),
        };
    }

    public formatBetsDays<T extends EnumBetType>(
        response: IBetHistoryResponseMap[T],
        timezone: number,
        dateFormat: string,
    ): IBetHistoryResponse['days'] {
        if ('betsByRun' in response) {
            return this.groupItemsByDay({
                items: response.betsByRun,
                timezone,
                dateFormat,
                timePath: 'run.time',
            });
        }

        if ('subscriptions' in response) {
            return this.groupItemsByDay({
                items: response.subscriptions,
                timezone,
                dateFormat,
                timePath: 'time',
            });
        }

        if ('bets' in response) {
            return this.groupItemsByDay({
                items: response.bets,
                timezone,
                dateFormat,
                timePath: 'time',
            });
        }

        return [];
    }
}

export const reportsFormatter = new ReportsFormatter();
